import { Num, Utils } from '../../../../utils/common'
import { DOMUtils } from '../../../../utils/dom'
import { Mixin } from '../../../../utils/mixin'
import {
    IIdentityProviderConfig,
    IIdentityServiceAdapter,
    ISubscriberProviderConfig,
    ISubscriberServiceAdapter,
    ISubscription,
    SSOScreen
} from '../adapter'

interface IPianoApiResponse {
    code: number
    count?: number
    data?: object[]
    limit: number
    offset: number
    total: number
    conversions?: IPianoConversion[]
}

interface IPianoApiAccessItem {
    expire_date?: number
    resource: {
        aid: string
        rid: string
    }
    start_date: number
}

export interface IPianoTerm {
    aid: string
    term_id: string
    resource?: object
    name?: string
    description?: string
    type?: string
}

export interface IPianoConversion {
    aid: string
    term?: IPianoTerm
    term_conversion_id?: string
    type?: string
}

interface IWindow {
    femPianoHack: (..._: any[]) => void
    janrain: any
    janrainCaptureWidgetOnLoad: () => void
    tp: any
}

declare let window: IWindow

// tslint:disable: max-func-body-length
// tslint:disable-next-line: no-big-function
export const WithPiano = <T extends Mixin<IIdentityServiceAdapter<IIdentityProviderConfig>>>(Base: T) =>
    class extends Base implements ISubscriberServiceAdapter {
        protected pianoEnabled = true
        protected pianoPreconfigured: boolean
        private pianoHack: {
            account?: string
            callback: keyof IWindow
            provider: string
            storeToken: string
        } = {
            callback: 'femPianoHack',
            provider: 'janrain',
            storeToken: 'janrainCaptureToken'
        }
        private pianoEndpoints = {
            access: '/access/list',
            conversions: '/conversion/list',
            register: '/conversion/registration/create',
            verify: '/anon/janrain/verifyAccountInfo'
        }
        private pianoExpUrl = '//experience.tinypass.com/xbuilder/experience/load?aid='
        private pianoReady: Promise<boolean>
        private registrantTermId: string
        private userToken: string

        public initialize(
            conf: ISubscriberProviderConfig,
            ssoCreds: IIdentityProviderConfig,
            isPremium = false,
            isPaywallWhitelisted = false) {
            // need to set this first since our overriding methods are called by super
            if (!conf) {
                this.pianoEnabled = false
            }
            this.pianoPreconfigured = !!window.tp

            super.initialize(ssoCreds) // calls JanrainAdapter intialize() method
            if (!this.pianoEnabled) {
                this.enableJanrainAdapter()
                return this
            }

            this.registrantTermId = conf.registrantTermId || ''
            this.userToken = ''

            // save for calling api without SDK
            this.pianoHack.account = conf.account

            void this.pianoPushTags(isPremium, isPaywallWhitelisted)

            /**
             * This may or may not be required in the future, but for sake of migrations,
             * we're not initializing when the global object is already present
             * (meaning the backend has presumably configured it loaded the js).
             * We still add the init handler, but don't perform the SSO integration.
             */
            if (this.pianoPreconfigured) {
                this.pianoReady = new Promise((resolve) =>
                    window.tp.push(['init', () => this.pianoOnInit(ssoCreds, resolve)]))
                return this
            }

             // setup global object
            window.tp = window.tp || []

            // configure (promised as SSO session found event can happen before Piano init)
            this.pianoReady = new Promise((resolve) =>
                window.tp.push(['init', () => this.pianoOnInit(ssoCreds, resolve)]))
            window.tp.push(['setAid', conf.account])

            // dev/stage only
            if (__NON_PROD) {
                window.tp.push(['setDebug', true])
            }

            // Add Piano aid to Piano experience script url
            this.pianoExpUrl = this.pianoExpUrl + conf.account

            // load Piano expereince JS (includes both composer and API JS to browser)
            DOMUtils.loadScript(this.pianoExpUrl)

            return this
        }

        /**
         * Fetches user subscriptions.
         */
        public async getAccess(): Promise<ISubscription[]> {
            // TODO: if/when able to properly await api, use:
            // const res = await this.pianoCallApi(this.pianoEndpoints.access)
            const res: IPianoApiResponse = await new Promise(async (resolve, reject) => {
                const ver: any = await this.pianoCallApi(this.pianoEndpoints.verify, {
                    janrain_capture_token: localStorage.getItem(this.pianoHack.storeToken),
                    url: location.href
                }).catch(reject)
                if (ver.code !== 0 || !(ver.access && ver.access.user && ver.access.user.valid)) {
                    return reject(ver)
                }
                window[this.pianoHack.callback] = (r: IPianoApiResponse) => r.code !== 0 ? reject(r) : resolve(r)
                const query = Utils.query({
                    _: Date.now(),
                    aid: this.pianoHack.account,
                    callback: this.pianoHack.callback,
                    cross_app: true,
                    tac: ver.access.access_token.value,
                    user_provider: this.pianoHack.provider,
                    user_token: ver.access.user_token
                })
                this.userToken = ver.access.user_token
                DOMUtils.loadScript(`${window.tp.getApiEndpoint()}${this.pianoEndpoints.access}?${query}`)
            })

            // TODO: catch rejects - retry logic? default to `false`?
            return (res.data || []).map((item: IPianoApiAccessItem) => {
                const sub = {
                    aid: item.resource.aid,
                    id: item.resource.rid,
                    start: item.start_date * Num.secToMs
                } as ISubscription

                // expiry not always present
                if (item.expire_date) {
                    sub.end = item.expire_date * Num.secToMs
                }

                return sub
            })
        }
        public registerInitHandler() {
            // Piano handles this, so override Janrain unless not enabled
            if (!this.pianoEnabled) {
                super.registerInitHandler()
            }
        }

        /**
         * Close Piano modal
         */
        public pianoCloseModal() {
            if (this.pianoEnabled) {
                window.tp.offer.close()
            }
        }

        /**
         * Get a list of user conversions
         */
        public async getConversions() {
            const query = {user_token: this.userToken}
            const conv = await this.pianoCallApi(this.pianoEndpoints.conversions, query)
            return (conv.conversions).map( (item) => {
                return item.term.term_id
            })
        }

        /**
         * Register a user for one-digital registant access, if they don't have it yet.
         */
        public async registerForOneDigital() {
            const query = {term_id: this.registrantTermId, user_token: this.userToken}
            await this.pianoCallApi(this.pianoEndpoints.register, query)
            window.tp.user.refreshAccessToken(true)
        }

        /**
         * Push Piano tags
         */
        private async pianoPushTags(isPremium: boolean, isPaywallWhitelisted: boolean) {
            const tags: string[] = []
            if (isPremium) {
                tags.push('premium_locked')
            }

            if (isPaywallWhitelisted) {
                tags.push('not_metered')
            } else {
                tags.push('metered')
            }

            await this.pianoReady
            return new Promise((resolve, reject) => {
                try {
                    window.tp.push(['setTags', tags])
                    resolve()
                } catch (e) {
                    reject(e)
                }
            })
        }

        /**
         * Calls Piano API via global object.
         */
        private async pianoCallApi(endpoint: string, data?: any): Promise<IPianoApiResponse> {
            await this.pianoReady
            return new Promise((resolve, reject) => {
                try {
                    window.tp.api.callApi(endpoint, data, (res: IPianoApiResponse) => {
                        if (res.code > 0) {
                            reject(new Error(`Piano API failure: ${JSON.stringify(res)}`))
                        } else {
                            resolve(res)
                        }
                    })
                } catch (e) {
                    reject(e)
                }
            })
        }

        private pianoOnInit(ssoCreds: IIdentityProviderConfig, resolve: () => void) {
            if (ssoCreds && !this.pianoPreconfigured) {
                window.tp.janrain.init({
                    appId: ssoCreds.account,
                    appName: ssoCreds.application,
                    clientId: ssoCreds.client,
                    loginRequiredScreen: SSOScreen.SIGN_IN,
                })
            }

            this.enableJanrainAdapter()

            resolve()
        }

        private enableJanrainAdapter() {
            // now we can enable Janrain adapter
            if (window.janrain.events) {
                super.enable()
            } else {
                const existingHandler = window.janrainCaptureWidgetOnLoad
                window.janrainCaptureWidgetOnLoad = () => {
                    super.enable()
                    if (existingHandler) {
                        existingHandler()
                    }
                }
            }
        }
    }
